/**
 * Neon-only data layer (Firebase Auth/Storage/FCM stay on Firebase).
 * No Firestore fallback — VITE_API_URL / same-origin /api must be the attendance-api.
 */
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, getApiBaseUrl, apiClient } from './apiClient'

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/

export class Timestamp {
  constructor(seconds, nanoseconds = 0) {
    this.seconds = seconds
    this.nanoseconds = nanoseconds
  }

  toDate() {
    return new Date(this.seconds * 1000 + this.nanoseconds / 1e6)
  }

  toMillis() {
    return this.seconds * 1000 + Math.floor(this.nanoseconds / 1e6)
  }

  toJSON() {
    return this.toDate().toISOString()
  }

  valueOf() {
    return this.toMillis()
  }

  static now() {
    return Timestamp.fromDate(new Date())
  }

  static fromDate(date) {
    const ms = date instanceof Date ? date.getTime() : new Date(date).getTime()
    return new Timestamp(Math.floor(ms / 1000), (ms % 1000) * 1e6)
  }

  static fromMillis(ms) {
    return new Timestamp(Math.floor(ms / 1000), (ms % 1000) * 1e6)
  }
}

export function serverTimestamp() {
  return { __op: 'serverTimestamp' }
}

export function arrayUnion(...elements) {
  return { __op: 'arrayUnion', elements }
}

export function arrayRemove(...elements) {
  return { __op: 'arrayRemove', elements }
}

export function deleteField() {
  return { __op: 'deleteField' }
}

function generateClientId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '')
  }
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

function reviveValue(value) {
  if (value == null) return value
  if (typeof value === 'string' && ISO_RE.test(value)) {
    const d = new Date(value)
    if (!Number.isNaN(d.getTime())) return Timestamp.fromDate(d)
  }
  if (Array.isArray(value)) return value.map(reviveValue)
  if (typeof value === 'object') {
    if (value.__op) return value
    const out = {}
    for (const [k, v] of Object.entries(value)) out[k] = reviveValue(v)
    return out
  }
  return value
}

function serializeValue(value) {
  if (value == null) return value
  if (value instanceof Timestamp) return value.toDate().toISOString()
  if (value instanceof Date) return value.toISOString()
  if (Array.isArray(value)) return value.map(serializeValue)
  if (typeof value === 'object') {
    if (value.__op) return value
    const out = {}
    for (const [k, v] of Object.entries(value)) out[k] = serializeValue(v)
    return out
  }
  return value
}

class DocumentSnapshot {
  constructor(id, data, existsFlag, collectionNameForRef = null) {
    this.id = id
    this._data = data
    this._exists = !!existsFlag
    // Firestore-compatible DocumentReference used by writeBatch.delete(doc.ref), etc.
    this.ref = collectionNameForRef ? doc(null, collectionNameForRef, id) : null
  }

  exists() {
    return this._exists
  }

  data() {
    return this._data ? reviveValue(this._data) : undefined
  }
}

class QuerySnapshot {
  constructor(docs) {
    this.docs = docs
    this.empty = docs.length === 0
    this.size = docs.length
  }

  forEach(cb) {
    this.docs.forEach(cb)
  }
}

function collectionName(ref) {
  if (typeof ref === 'string') return ref
  if (ref?.type === 'query') return ref.collection
  if (ref?.type === 'collection') return ref.name || ref.path
  if (ref?.type === 'doc') return ref.collection
  return ref?.path || ref?.name || ref?._collection || ref?.collection
}

function ensureApi() {
  const base = getApiBaseUrl()
  if (!base) {
    throw new Error('API base URL is not configured')
  }
}

export function collection(_db, name) {
  return { type: 'collection', name, path: name, id: name }
}

/**
 * Firestore-compatible doc() reference.
 * - doc(db, 'users', id)
 * - doc(collectionRef, id)
 * - doc(collectionRef) → auto-generated id (tasks/payroll close rely on this)
 */
export function doc(dbOrCol, colOrId, maybeId) {
  let name
  let id
  if (maybeId !== undefined) {
    name = typeof colOrId === 'string' ? colOrId : collectionName(colOrId)
    id = maybeId
  } else if (dbOrCol?.type === 'collection') {
    name = dbOrCol.name
    // Firestore: doc(collectionRef) auto-generates an id; doc(collectionRef, id) uses the id
    id = colOrId !== undefined ? colOrId : generateClientId()
  } else {
    name = typeof dbOrCol === 'string' ? dbOrCol : collectionName(dbOrCol)
    id = colOrId
  }
  return { type: 'doc', collection: name, id, path: `${name}/${id}` }
}

export function where(field, op, value) {
  let v = value
  if (v instanceof Timestamp) v = v.toDate().toISOString()
  if (v instanceof Date) v = v.toISOString()
  return { type: 'where', field, op, value: v }
}

export function orderBy(field, direction = 'asc') {
  return { type: 'orderBy', field, direction }
}

export function limit(n) {
  return { type: 'limit', value: n }
}

/**
 * Build or extend a query.
 * Supports nested composition: query(existingQuery, where(...))
 */
export function query(collectionOrQueryRef, ...constraints) {
  if (collectionOrQueryRef?.type === 'query') {
    return {
      type: 'query',
      collection: collectionOrQueryRef.collection,
      constraints: [...(collectionOrQueryRef.constraints || []), ...constraints],
    }
  }
  return {
    type: 'query',
    collection: collectionName(collectionOrQueryRef),
    constraints,
  }
}

function queryToParams(q) {
  const filters = []
  let orderByField = null
  let orderDirection = 'asc'
  let limitCount = null
  for (const c of q.constraints || []) {
    if (c.type === 'where') filters.push({ field: c.field, op: c.op, value: c.value })
    else if (c.type === 'orderBy') {
      orderByField = c.field
      orderDirection = c.direction || 'asc'
    } else if (c.type === 'limit') limitCount = c.value
  }
  const params = {}
  if (filters.length) params.filters = JSON.stringify(filters)
  if (orderByField) {
    params.orderBy = orderByField
    params.orderDirection = orderDirection
  }
  if (limitCount != null) params.limit = limitCount
  return params
}

export async function getDocs(collectionOrQuery) {
  ensureApi()
  const name =
    collectionOrQuery.type === 'query'
      ? collectionOrQuery.collection
      : collectionName(collectionOrQuery)
  const params = collectionOrQuery.type === 'query' ? queryToParams(collectionOrQuery) : {}
  const data = await apiGet(`/v1/${name}`, { params })
  const docs = (data.docs || []).map((d) => {
    const { id, ...rest } = d
    return new DocumentSnapshot(id, rest, true, name)
  })
  return new QuerySnapshot(docs)
}

export async function getDoc(docRef) {
  ensureApi()
  try {
    const data = await apiGet(`/v1/${docRef.collection}/${docRef.id}`)
    const { id, ...rest } = data.doc
    return new DocumentSnapshot(id, rest, true, docRef.collection)
  } catch (err) {
    if (err?.response?.status === 404) {
      return new DocumentSnapshot(docRef.id, null, false, docRef.collection)
    }
    throw err
  }
}

export async function addDoc(collectionRef, data) {
  ensureApi()
  const name = collectionName(collectionRef)
  const res = await apiPost(`/v1/${name}`, serializeValue(data))
  return doc(null, name, res.doc.id)
}

export async function setDoc(docRef, data, options = {}) {
  ensureApi()
  if (options.merge) {
    await apiPatch(`/v1/${docRef.collection}/${docRef.id}`, serializeValue(data))
  } else {
    await apiPut(`/v1/${docRef.collection}/${docRef.id}`, serializeValue(data))
  }
}

export async function updateDoc(docRef, data) {
  ensureApi()
  await apiPatch(`/v1/${docRef.collection}/${docRef.id}`, serializeValue(data))
}

export async function deleteDoc(docRef) {
  ensureApi()
  await apiDelete(`/v1/${docRef.collection}/${docRef.id}`)
}

export function writeBatch(_db) {
  const operations = []
  return {
    set(docRef, data, options = {}) {
      operations.push({
        type: 'set',
        collection: docRef.collection,
        id: docRef.id,
        data: serializeValue(data),
        merge: !!options.merge,
      })
    },
    update(docRef, data) {
      operations.push({
        type: 'update',
        collection: docRef.collection,
        id: docRef.id,
        data: serializeValue(data),
      })
    },
    delete(docRef) {
      operations.push({
        type: 'delete',
        collection: docRef.collection,
        id: docRef.id,
      })
    },
    async commit() {
      ensureApi()
      await apiPost('/v1/batch', { operations })
    },
  }
}

